import type { WhatsAppTemplate } from "../types";

/**
 * Turning a template + its filled parameters into the message a recipient sees.
 *
 * Pure and UI-free, so the composer can re-render the preview on every
 * keystroke without a round trip, and so the same rendering can be asserted in
 * tests. `WhatsAppMessageBubble` draws whatever this returns.
 */

export type WhatsAppPreviewButton = {
  type: string;
  text: string;
  url?: string;
};

export type WhatsAppMessageParts = {
  header: string;
  body: string;
  footer: string;
};

export const MEDIA_HEADER_FORMATS = ["IMAGE", "DOCUMENT", "VIDEO"] as const;

function isTemplateComponent(value: unknown): value is {
  type?: unknown;
  text?: unknown;
  format?: unknown;
  buttons?: unknown;
} {
  return typeof value === "object" && value !== null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Substitute `{{1}}`-style placeholders with the operator's values.
 *
 * An unfilled placeholder stays visible as `{{1}}` rather than collapsing to an
 * empty string — a gap in the preview reads as "this line is fine", which is
 * exactly the mistake the preview exists to prevent.
 */
export function renderTemplatePart(
  text: string,
  component: "header" | "body" | "footer",
  parameters: Record<string, string>,
): string {
  return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (_, index: string) => {
    const value = parameters[`${component}_${index}`]?.trim();
    return value || `{{${index}}}`;
  });
}

export function getTemplateText(
  template: WhatsAppTemplate,
  componentType: "header" | "body" | "footer",
): string {
  if (componentType === "header" && template.header_text) {
    return template.header_text;
  }
  if (componentType === "body" && template.body_text) {
    return template.body_text;
  }
  if (componentType === "footer" && template.footer_text) {
    return template.footer_text;
  }

  const component = (template.components ?? []).find((item) => {
    if (!isTemplateComponent(item)) return false;
    return String(item.type ?? "").toLowerCase() === componentType;
  });

  return isTemplateComponent(component) && typeof component.text === "string"
    ? component.text
    : "";
}

export function renderTemplateMessageParts(
  template: WhatsAppTemplate,
  parameters: Record<string, string>,
): WhatsAppMessageParts {
  return {
    header: renderTemplatePart(
      getTemplateText(template, "header"),
      "header",
      parameters,
    ),
    body: renderTemplatePart(
      getTemplateText(template, "body"),
      "body",
      parameters,
    ),
    footer: renderTemplatePart(
      getTemplateText(template, "footer"),
      "footer",
      parameters,
    ),
  };
}

/**
 * The template's buttons, from either the flat `buttons` field or the nested
 * BUTTONS component — Meta returns one shape or the other depending on whether
 * the template came from the library or from our own create call.
 */
export function getTemplateButtons(
  template: WhatsAppTemplate,
): WhatsAppPreviewButton[] {
  const directButtons = Array.isArray(template.buttons)
    ? template.buttons
    : null;
  const buttonComponent = (template.components ?? []).find((item) => {
    if (!isTemplateComponent(item)) return false;
    return String(item.type ?? "").toUpperCase() === "BUTTONS";
  });
  const componentButtons =
    isTemplateComponent(buttonComponent) &&
    Array.isArray(buttonComponent.buttons)
      ? buttonComponent.buttons
      : null;

  return (directButtons ?? componentButtons ?? [])
    .filter(isRecord)
    .map((button) => ({
      type: String(button.type ?? "QUICK_REPLY").toUpperCase(),
      text: String(button.text ?? "").trim(),
      url: typeof button.url === "string" ? button.url : undefined,
    }))
    .filter((button) => button.text);
}

export function getHeaderMediaFormat(
  template: WhatsAppTemplate,
): string | null {
  const formats = MEDIA_HEADER_FORMATS as readonly string[];
  const directFormat = String(template.header_format ?? "").toUpperCase();
  if (formats.includes(directFormat)) return directFormat;
  const header = (template.components ?? []).find((item) => {
    if (!isTemplateComponent(item)) return false;
    return String(item.type ?? "").toUpperCase() === "HEADER";
  });
  const format = isTemplateComponent(header)
    ? String(header.format ?? "").toUpperCase()
    : "";
  return formats.includes(format) ? format : null;
}

/**
 * Placeholders the operator has not filled yet, as `{{n}}` labels.
 *
 * The composer surfaces these so an unfinished message is obvious while typing
 * rather than at send time.
 */
export function unfilledPlaceholders(
  template: WhatsAppTemplate,
  parameters: Record<string, string>,
): string[] {
  const missing: string[] = [];
  for (const component of ["header", "body", "footer"] as const) {
    const text = getTemplateText(template, component);
    for (const match of text.matchAll(/\{\{\s*(\d+)\s*\}\}/g)) {
      const index = match[1];
      if (!parameters[`${component}_${index}`]?.trim()) {
        missing.push(`{{${index}}}`);
      }
    }
  }
  return missing;
}
